import Editor, { useMonaco } from "@monaco-editor/react";
import { BoldIcon, CodeIcon, ItalicIcon, LinkIcon, OrderedListIcon, StrikeIcon, TableIcon, UnorderedListIcon } from "../../forms/formBuilder/PropertiesPanel/asset/MarkdownIcon";
import React, { useEffect, useRef, useState } from "react";
import { AgentDetails } from "./types";
import style from "./style.module.scss"

interface IEditor {
    source: string;
    height: string;
    type: any;
    agentDetails: AgentDetails;
    setSourceData: (value: AgentDetails) => void;
    canEditAgents: boolean
    setErrors:(v: any) => void;
    setPrompt: (v: string) => void;
}

export const EditorPanel: React.FC<IEditor> = ({
    source,
    type,
    height,
    agentDetails,
    setSourceData,
    setPrompt,
    setErrors,
    canEditAgents
}) => {
    const additionalOptions = {
        overviewRulerLanes: 0,
        overviewRulerBorder: false,
    };
    const instanceRef: any = useRef(null);
    const editorRef: any = instanceRef;
    const monaco = useMonaco();
    let [highlightText, setHighlightText] = useState<string>("");
    let [selectedText, setSelectedText] = useState<string>("");

    // init monaco editor
    function handleEditorDidMount(fromEditor: any) {
        editorRef.current = fromEditor;
        if (!fromEditor) return;
        const disposable = fromEditor.onDidChangeCursorPosition(onMouseDown);
        return () => {
            disposable.dispose();
        };
    }

    const onMouseDown = (e: any) => {
        const editor = editorRef.current;
        let position = editor.getPosition();
        const selection = editor.getSelection();
        const lineNumber = position.lineNumber;
        const selectedContent = editor.getModel().getValueInRange(selection);
        const lineContent = editor.getModel().getLineContent(lineNumber);
        if (!lineContent) {
            setHighlightText("")
        } else if (lineContent.startsWith("######")) {
            setHighlightText("H6")
        } else if (lineContent.startsWith("#####")) {
            setHighlightText("H5")
        } else if (lineContent.startsWith("####")) {
            setHighlightText("H4")
        } else if (lineContent.startsWith("###")) {
            setHighlightText("H3")
        } else if (lineContent.startsWith("##")) {
            setHighlightText("H2")
        } else if (lineContent.startsWith("#")) {
            setHighlightText("H1")
        } else {
            setHighlightText("")
        }

        let hasBoldItalicStrikeCombo = false;
        let hasBoldStrikeCombo = false;
        let hasItalicStrikeCombo = false;
        let hasBoldItalicCombo = false;
        let hasBoldCombo = false;
        let hasItalicCombo = false;
        let hasStrikeCombo = false;

        if (selectedContent) {
            hasBoldItalicStrikeCombo = selectedContent.includes("***~~") && selectedContent.includes("~~***");
            hasBoldStrikeCombo = selectedContent.includes("**~~") && selectedContent.includes("~~**");
            hasItalicStrikeCombo = selectedContent.includes("*~~") && selectedContent.includes("~~*");
            hasBoldItalicCombo = selectedContent.includes("***") && selectedContent.includes("***");
            hasBoldCombo = selectedContent.includes("**") && selectedContent.includes("**") && !hasBoldItalicStrikeCombo && !hasBoldItalicCombo;
            hasItalicCombo = selectedContent.includes("*") && selectedContent.includes("*") && !hasBoldItalicStrikeCombo && !hasBoldItalicCombo;
            hasStrikeCombo = selectedContent.includes("~~") && selectedContent.includes("~~") && !hasBoldItalicStrikeCombo;
        } else {
            setSelectedText("");
        }

        // Set the selected text based on the combinations
        if (hasBoldItalicStrikeCombo) {
            setSelectedText("BOLD_ITALIC_STRIKE");
        } else if (hasBoldStrikeCombo) {
            setSelectedText("BOLD_STRIKE");
        } else if (hasItalicStrikeCombo) {
            setSelectedText("ITALIC_STRIKE");
        } else if (hasBoldItalicCombo) {
            setSelectedText("BOLD_ITALIC");
        } else if (hasBoldCombo) {
            setSelectedText("BOLD");
        } else if (hasItalicCombo) {
            setSelectedText("ITALIC");
        } else if (hasStrikeCombo) {
            setSelectedText("STRIKE");
        } else {
            setSelectedText("");
        }
    };

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;
        const disposable = editor.onDidChangeCursorPosition(onMouseDown);
        return () => {
            disposable.dispose();
        };
    }, [monaco]);

    function handleChange(e: any) {
        const editor = editorRef.current;
        setPrompt(e)
        setErrors((prevState: any) => { return { ...prevState, prompt: "" } })
        const disposable = editor.onMouseDown(onMouseDown);
        return () => {
            disposable.dispose();
        };
    }

    function handleWidget(proFrom: string) {
        const editor = editorRef?.current;
        if (!editor) return;

        const selection = editor.getSelection();
        const text = editor.getModel().getValueInRange(selection);
        const position = editor.getPosition();
        const lineNumber = position.lineNumber;
        const lineContent = editor.getModel().getLineContent(lineNumber);

        const markdownCommands: Record<string, string | (() => string | null)> = {
            BOLD: () => {
                const newText = text.includes("**") ? text.replace(/\*\*/g, '') : `**${text}**`;
                return newText;
            },
            ITALIC: () => {
                const newText = text.includes("*") ? text.replace(/\*/g, '') : `*${text}*`;
                return newText;
            },
            STRIKE: () => {
                const newText = text.includes("~~") ? text.replace(/~~/g, '') : `~~${text}~~`;
                return newText;
            },
            H1: () => {
                const headingContent = lineContent.replace(/^[\s#]+/, "").trimStart();
                const newText = highlightText === "H1" ? headingContent : `# ${headingContent}`;
                setHighlightText(highlightText === "H1" ? "" : "H1");
                return newText;
            },
            H2: () => {
                const headingContent = lineContent.replace(/^[\s#]+/, "").trimStart();
                const newText = highlightText === "H2" ? headingContent : `## ${headingContent}`;
                setHighlightText(highlightText === "H2" ? "" : "H2");
                return newText;
            },
            H3: () => {
                const headingContent = lineContent.replace(/^[\s#]+/, "").trimStart();
                const newText = highlightText === "H3" ? headingContent : `### ${headingContent}`;
                setHighlightText(highlightText === "H3" ? "" : "H3");
                return newText;
            },
            H4: () => {
                const headingContent = lineContent.replace(/^[\s#]+/, "").trimStart();
                const newText = highlightText === "H4" ? headingContent : `#### ${headingContent}`;
                setHighlightText(highlightText === "H4" ? "" : "H4");
                return newText;
            },
            H5: () => {
                const headingContent = lineContent.replace(/^[\s#]+/, "").trimStart();
                const newText = highlightText === "H5" ? headingContent : `##### ${headingContent}`;
                setHighlightText(highlightText === "H5" ? "" : "H5");
                return newText;
            },
            H6: () => {
                const headingContent = lineContent.replace(/^[\s#]+/, "").trimStart();
                const newText = highlightText === "H6" ? headingContent : `###### ${headingContent}`;
                setHighlightText(highlightText === "H6" ? "" : "H6");
                return newText;
            },
            UL: () => addList(text.trim(), "*"),
            OL: () => addList(text.trim(), "number"),
            CODE: () => `${"\n```\n"}${text}${"\n```\n"}`,
            LINK: () => {
                let linkText = `${text}[${"`"} ${"`"}]( )`;
                if (text && isValidUrl(text)) {
                    const domain = new URL(text).hostname.replace("www.", "");
                    linkText = `[${"`"}${domain}${"`"}](${text})`;
                }
                return linkText;
            },
            TABLE: () => '\n |  |  |\n |--|--|\n |  |  |',
        };

        const command = markdownCommands[proFrom];
        if (typeof command === "function") {
            const newText = command();
            if (newText && monaco) {
                const range = ['BOLD', 'ITALIC', 'STRIKE', 'UL', 'OL', 'LINK', 'TABLE', 'CODE'].includes(proFrom) ? selection : new monaco.Range(lineNumber, 1, lineNumber, lineContent.length + 1);
                editor.executeEdits("", [{
                    range: range,
                    text: newText,
                    forceMoveMarkers: true,
                }]);
            }
        }
    }

    const isValidUrl = (urlString: string) => {
        const urlPattern =
            /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;
        return urlPattern.test(urlString);
    };

    const addList = (inputString: string, mode: string) => {
        const lines = inputString.split("\n");
        let isNum = mode === "number" ? true : false;
        const outputLines = lines
            .map((line, index) => {
                let ol = `${index + 1}.`;
                let proVal = isNum ? ol : mode;
                if (line?.slice(0, proVal.length) !== `${proVal}`) {
                    return `${proVal} ${line.trim()}`;
                } else {
                    let prefix = `${proVal} `;
                    if (line?.startsWith(prefix)) {
                        line = line.replace(prefix, "");
                    }
                    return `${line.trim()}`;
                }
            })
            .join("\n");
        return outputLines;
    };

    return (
        <>
            <div
                style={{
                    margin: "10px 16px",
                    // padding: "6px 2px",
                    borderRadius: "8px",
                    display: "flex",
                    flexDirection: "row",
                    width: 695,
                    background: "#f3f4f6",
                }}
            >
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        onClick={() => handleWidget("BOLD")}
                    >
                        <div 
                            style={{ background: selectedText?.includes("BOLD") ? "#e5e7eb" : ""}}
                        >
                            <BoldIcon />
                        </div>
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        onClick={() => handleWidget("ITALIC")}
                    >
                        <div style={{ background: `${selectedText?.includes("ITALIC") ? "rgb(243 244 246)" : ""}`}}>
                            <ItalicIcon />
                        </div>
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        onClick={() => handleWidget("STRIKE")}
                    >
                        <div style={{ background: `${selectedText?.includes("STRIKE") ? "e5e7eb" : ""}` }}>
                            <StrikeIcon />
                        </div>
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        style={{
                            padding: "3px 0px",
                            lineHeight: "22px",
                        }}
                        onClick={() => handleWidget("H1")}
                    >
                        <span 
                            style={{ color: "#000000", padding: "6px", background: highlightText === "H1" ? "e5e7eb" : "" }}>
                            H1
                        </span>
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        style={{
                            padding: "3px 0px",
                            lineHeight: "22px",
                        }}
                        onClick={() => handleWidget("H2")}
                    >
                        <span 
                            style={{ color: "#000000", padding: "6px", background: highlightText === "H2" ? "#e5e7eb" :  ""}}>
                            H2
                        </span>
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        style={{
                            padding: "3px 0px",
                            lineHeight: "22px",
                        }}
                        onClick={() => handleWidget("H3")}
                    >
                        <span 
                            style={{ background: highlightText === "H3" ? "#e5e7eb" : ""}}
                        >
                            H3
                        </span>
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        style={{
                            padding: "3px 0px",
                            lineHeight: "22px",
                        }}
                        onClick={() => handleWidget("H4")}
                    >
                        <span 
                            style={{
                                background: highlightText === "H4" ? "#e5e7eb" : "", color: "#000000", padding: "6px" }}>
                            H4
                        </span>
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        style={{
                            padding: "3px 0px",
                            lineHeight: "22px",
                        }}
                        onClick={() => handleWidget("H5")}
                    >
                        <span
                            style={{ background: highlightText === "H5" ? "#e5e7eb" : "", color: "#000000", padding: "6px" }}
                        >
                            H5
                        </span>
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        style={{
                            padding: "3px 0px",
                            lineHeight: "22px",
                        }}
                        onClick={() => handleWidget("H6")}
                    >
                        <span 
                            style={{ background: highlightText === "H6" ? "#e5e7eb" : "", color: "#000000", padding: "6px" }}
                        >
                            H6
                        </span>
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        onClick={() => handleWidget("UL")}
                    >
                        <UnorderedListIcon />
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        onClick={() => handleWidget("OL")}
                    >
                        <OrderedListIcon />
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        onClick={() => handleWidget("LINK")}
                    >
                        <LinkIcon />
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        onClick={() => handleWidget("CODE")}
                    >
                        <CodeIcon />
                    </button>
                </div>
                <div className={style.agentMarkdownEditorStyles}>
                    <button
                        onClick={() => handleWidget("TABLE")}
                    >
                        <TableIcon />
                    </button>
                </div>
            </div>
            <Editor
                defaultValue={source}
                value={source}
                language={type}
                path={type}
                className="agent-instructions-editor"
                width={710}
                height={height}
                line={100}
                onMount={handleEditorDidMount}
                options={{
                    readOnly: !canEditAgents,
                    fontSize: 14,
                    trimAutoWhitespace: true,
                    renameOnType: true,
                    autoIndent: "full",
                    autoClosingBrackets: "languageDefined",
                    lineNumbers: "off",
                    scrollBeyondLastLine: false,
                    quickSuggestions: {
                        other: true,
                        comments: true,
                        strings: true,
                    },
                    parameterHints: {
                        enabled: true,
                    },
                    suggestOnTriggerCharacters: true,
                    acceptSuggestionOnEnter: "on",
                    tabCompletion: "on",
                    wordBasedSuggestions: true,
                    selectOnLineNumbers: true,
                    roundedSelection: true,
                    cursorStyle: "line",
                    minimap: {
                        enabled: false,
                    },
                    folding: false,
                    wordWrap: "on",
                    ...additionalOptions,
                }}
                keepCurrentModel={false}
                onChange={handleChange}
            />
        </>
    );
};